import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Loader2, CreditCard } from "lucide-react";
import { toast } from "sonner";

export default function CheckoutPage() {
  const navigate = useNavigate();
  const [cart, setCart] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [shipping, setShipping] = useState({
    full_name: "",
    address: "",
    zipcode: "",
    city: "",
    country: "France",
  });

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/cart");
        if (!data.items?.length) {
          navigate("/app/cart");
          return;
        }
        setCart(data);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [navigate]);

  const update = (k) => (e) => setShipping({ ...shipping, [k]: e.target.value });

  const handlePay = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/orders/checkout", {
        shipping_address: shipping,
        payment_method: "stripe_test",
      });
      toast.success("Commande confirmée", { description: `N° ${data.id.slice(0, 8)}` });
      navigate(`/app/account?order=${data.id}`);
    } catch (e) {
      toast.error("Erreur de paiement", { description: e.response?.data?.detail || "Réessayez" });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || !cart) {
    return <div className="py-20 flex justify-center"><Loader2 className="h-7 w-7 animate-spin text-brand-green" /></div>;
  }

  return (
    <div data-testid="checkout-page">
      <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-brand-green mb-2">Validation</div>
      <h1 className="text-4xl font-black tracking-tighter text-white">Paiement</h1>

      <div className="mt-10 grid lg:grid-cols-3 gap-8">
        <form onSubmit={handlePay} className="lg:col-span-2 space-y-4" data-testid="checkout-form">
          <div className="border border-brand-blue/40 rounded-sm p-6 space-y-4">
            <h2 className="text-[10px] uppercase tracking-[0.3em] font-bold text-brand-green">Adresse de livraison</h2>
            <Field label="Nom complet" testId="checkout-name" value={shipping.full_name} onChange={update("full_name")} required />
            <Field label="Adresse" testId="checkout-address" value={shipping.address} onChange={update("address")} required />
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Code postal" testId="checkout-zip" value={shipping.zipcode} onChange={update("zipcode")} required />
              <Field label="Ville" testId="checkout-city" value={shipping.city} onChange={update("city")} required />
            </div>
            <Field label="Pays" testId="checkout-country" value={shipping.country} onChange={update("country")} required />
          </div>

          <div className="border border-brand-blue/40 rounded-sm p-6">
            <h2 className="text-[10px] uppercase tracking-[0.3em] font-bold text-brand-green mb-3">Paiement</h2>
            <div className="flex items-center gap-3 px-4 py-4 border border-brand-lightBlue/40 rounded-sm bg-brand-navy/40">
              <CreditCard className="h-5 w-5 text-brand-green" />
              <div>
                <div className="font-bold text-white text-sm">Stripe (mode test)</div>
                <div className="text-xs text-brand-gray/70 font-roboto">
                  L'intégration Stripe sera connectée en production. Mode démo.
                </div>
              </div>
            </div>
          </div>

          <button
            type="submit"
            disabled={submitting}
            data-testid="checkout-pay-button"
            className="w-full inline-flex items-center justify-center gap-2 h-12 rounded-sm bg-brand-green text-black font-bold hover:bg-brand-green/90 disabled:opacity-50 transition shadow-glow-green"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Confirmer & payer {cart.total.toFixed(2)} €</>}
          </button>
        </form>

        <aside className="border border-brand-blue/40 rounded-sm p-6 h-fit" data-testid="checkout-summary">
          <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-brand-green mb-3">Récapitulatif</div>
          <ul className="space-y-3 text-sm">
            {cart.items.map((it) => (
              <li key={it.product_id} className="flex justify-between gap-2 text-brand-gray font-roboto">
                <span className="truncate">{it.name} × {it.quantity}</span>
                <span className="text-white font-bold whitespace-nowrap">{it.line_total.toFixed(2)} €</span>
              </li>
            ))}
          </ul>
          <div className="mt-4 pt-4 border-t border-brand-blue/30 flex justify-between items-baseline">
            <span className="font-bold text-white">Total</span>
            <span className="font-black text-xl text-white">{cart.total.toFixed(2)} €</span>
          </div>
        </aside>
      </div>
    </div>
  );
}

function Field({ label, testId, ...props }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-[0.25em] font-bold text-brand-gray mb-2">{label}</span>
      <input
        {...props}
        data-testid={testId}
        className="w-full h-11 px-4 bg-black border border-brand-blue/60 rounded-sm text-white placeholder-white/30 focus:outline-none focus:border-brand-green font-roboto"
      />
    </label>
  );
}
